/**
 * A company's labour figures, from the server since 2026-09-25 (backend/services/labourSettings.js):
 * the employer's rates, the wage target, the staffing need per weekday and hour, and sales per day.
 * They were in each browser (shell/local.ts), so two managers could see two labour costs and no
 * other client saw them at all. The hooks give the same shapes the screens used, so a screen only
 * swaps where it reads from.
 */
import { get, put } from '@/api/client';
import { useCan } from '@/auth/caps';
import type { Rates } from '@/rules/labour';
import type { NeedProfile, Sales } from '@/shell/local';
import { useTenant } from '@/shell/tenant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

interface Figure {
  value: number;
  set: boolean;
}
/** GET /admin/labour-settings; the rates and target only for a reader with pay.view. */
export interface LabourSettings {
  revision: number;
  staffing_need: Record<string, Record<string, number>>;
  rates?: { employer_tax_pct: Figure; pension_pct: Figure; holiday_pay_pct: Figure };
  wage_target_pct?: Figure;
}

/** The server's names to the ones rules/labour.ts uses, only for figures the company set. */
export function ratesFrom(s: LabourSettings | undefined): Partial<Rates> {
  const r = s?.rates;
  if (!r) return {};
  const out: Partial<Rates> = {};
  if (r.employer_tax_pct.set) out.agaPct = r.employer_tax_pct.value;
  if (r.pension_pct.set) out.pensionPct = r.pension_pct.value;
  if (r.holiday_pay_pct.set) out.holidayPct = r.holiday_pay_pct.value;
  return out;
}

/** need[dow][hour] with numeric keys, as shell/local.ts NeedProfile. */
export function needFrom(s: LabourSettings | undefined): NeedProfile {
  const out: NeedProfile = {};
  for (const [dow, hours] of Object.entries(s?.staffing_need ?? {})) {
    const day: Record<number, number> = {};
    for (const [h, n] of Object.entries(hours)) day[Number(h)] = n;
    out[Number(dow)] = day;
  }
  return out;
}

const KEY = (tenantId: number | undefined) => ['labour-settings', tenantId];

/** The company's figures. Everyone who reads the schedule reads the need; the money needs pay.view. */
export function useLabour() {
  const { tenant } = useTenant();
  const can = useCan();
  const q = useQuery({
    queryKey: KEY(tenant?.id),
    queryFn: () => get<LabourSettings>('/admin/labour-settings'),
    enabled: Boolean(tenant?.id) && can('vaktplan.se'),
    staleTime: 60_000,
  });
  return {
    ready: q.isSuccess,
    query: q,
    // on its way (a role without the schedule never asks: idle, not pending), or refused (#262, #263)
    pending: q.isPending && q.fetchStatus !== 'idle',
    failed: q.isError,
    retry: () => q.refetch(),
    settings: q.data,
    revision: q.data?.revision ?? 0,
    rates: ratesFrom(q.data),
    need: needFrom(q.data),
    target: q.data?.wage_target_pct?.value ?? 30,
  };
}

/** Save some figures; the revision is the one last read, and a stale one is the server's 409. */
export function useSaveLabour() {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown> & { revision: number }) =>
      put<LabourSettings>('/admin/labour-settings', patch),
    onSuccess: (s) => qc.setQueryData(KEY(tenant?.id), s),
    onError: () => qc.invalidateQueries({ queryKey: KEY(tenant?.id) }),
  });
}

/** Rates as the server names them, for a save. */
export const ratesTo = (r: Partial<Rates>) => ({
  ...(r.agaPct !== undefined ? { employer_tax_pct: r.agaPct } : {}),
  ...(r.pensionPct !== undefined ? { pension_pct: r.pensionPct } : {}),
  ...(r.holidayPct !== undefined ? { holiday_pay_pct: r.holidayPct } : {}),
});

/** Sales from `from` to `to`, both included. */
export function useSales(from: string, to: string) {
  const { tenant } = useTenant();
  const can = useCan();
  const q = useQuery({
    queryKey: ['sales', tenant?.id, from, to],
    queryFn: () => get<{ days: Sales }>(`/admin/sales?from=${from}&to=${to}`),
    enabled: Boolean(tenant?.id) && can('vaktplan.kost'),
  });
  return {
    ready: q.isSuccess,
    pending: q.isPending && q.fetchStatus !== 'idle',
    failed: q.isError,
    retry: () => q.refetch(),
    sales: q.data?.days ?? {},
  };
}

/** Save many days at once, all or nothing; null removes a day. */
export function useSaveSales() {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: Record<string, number | null>) => put<{ saved: number }>('/admin/sales', { days }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['sales', tenant?.id] }),
  });
}

/** Each day's actual sales, the manager's forecast and the 4-week estimate (GET /admin/sales/forecast, 2026-09-27). */
export function useForecast(from: string, to: string, enabled = true) {
  const { tenant } = useTenant();
  const can = useCan();
  const q = useQuery({
    queryKey: ['sales-forecast', tenant?.id, from, to],
    queryFn: () =>
      get<{
        days: Record<string, { actual: number | null; forecast: number | null; estimate: number | null }>;
      }>(`/admin/sales/forecast?from=${from}&to=${to}`),
    enabled: enabled && Boolean(tenant?.id) && can('vaktplan.kost'),
  });
  return { ready: q.isSuccess, days: q.data?.days ?? {} };
}

/** One day's figure: a coming day's forecast, or a past day's actual sales; null clears it. */
export function useSaveDaySales() {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ day, value, forecast }: { day: string; value: number | null; forecast: boolean }) =>
      put<{ saved: number }>(forecast ? '/admin/sales/forecast' : '/admin/sales', { days: { [day]: value } }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['sales-forecast', tenant?.id] });
      qc.invalidateQueries({ queryKey: ['sales', tenant?.id] });
    },
  });
}

/** One day of GET /admin/labour-cost?supplements=1 (2026-10-25): the plan and what was clocked, with supplements. */
/** A side's cost by kind (#207): base pay, each supplement type by name, and feriepenger, pension and AGA on top. */
export interface CostParts {
  base: number;
  supplements: { name: string; cost: number }[];
  on_top: number;
}
export interface CostDay {
  date: string;
  sales: number | null;
  planned: { hours: number; cost: number; pct: number | null; parts?: CostParts };
  actual: { shifts: number; hours: number; cost: number; parts?: CostParts };
  unpriced: number;
}
/** The employer's cost per day, planned and clocked, the company's date and time pay rules included. */
export function useLabourCost(from: string, to: string, enabled = true) {
  const { tenant } = useTenant();
  const can = useCan();
  const q = useQuery({
    queryKey: ['labour-cost', tenant?.id, from, to],
    queryFn: () =>
      get<{ days: CostDay[]; target_pct: number | null }>(
        `/admin/labour-cost?from=${from}&to=${to}&supplements=1`,
      ),
    enabled: enabled && Boolean(tenant?.id) && can('vaktplan.kost'),
  });
  return {
    ready: q.isSuccess,
    days: new Map((q.data?.days ?? []).map((d) => [d.date, d])),
    target: q.data?.target_pct ?? null,
  };
}

/** The month's labour budget from PowerOffice Go (GET /admin/poweroffice/budget?month=, #207); null when none. */
export function useMonthBudget(ym: string, enabled: boolean) {
  const { tenant } = useTenant();
  const can = useCan();
  const [y, m] = ym.split('-').map(Number);
  const q = useQuery({
    queryKey: ['poweroffice', 'budget', tenant?.id, ym],
    queryFn: () => get<{ labour_cost: number }>(`/admin/poweroffice/budget?year=${y}&month=${m}`),
    enabled: enabled && Boolean(tenant?.id) && can('vaktplan.kost'),
    retry: false,
    staleTime: 5 * 60_000,
  });
  return q.data && q.data.labour_cost > 0 ? q.data.labour_cost : null;
}
